import React from 'react';
import type { NearbyPipeline } from '../../lib/api';

// Per-parcel curated overrides for v2 briefing. Mirrors the v1
// parcel-overrides logic in src/components/briefing/BriefingContent.tsx
// (PACES_PARCEL_IDS + PACES_PRECEDENTS) — keep both in sync when content
// changes. Drop a parcel into this file when the auto-derived blocks
// can't sell the site.

export interface CuratedPrecedent {
  name: string;
  highlight?: boolean;
  distance: string;
  capacity: string;
  operator: string;
  status: string;
  location: string;
  detail: string;
}

export interface PrecedentCompareRow {
  label: string;
  sub?: string;
  context?: string;
  capacity: string;
  acres: string;
  acPerGw: string;
  gas: string;
  permit: string;
  land: string;
  inAoi?: boolean;
  emphasis?: boolean;
}

export interface PrecedentCompare {
  intro?: React.ReactNode;
  rows: PrecedentCompareRow[];
  takeaway?: React.ReactNode;
}

export interface SiteIntelBlock {
  heading: string;
  body: React.ReactNode;
}

export interface ParcelOverrides {
  siteIntelligence: SiteIntelBlock[];
  precedents: CuratedPrecedent[];
  precedentsMeta: string;
  precedentCompare?: PrecedentCompare;
  logistics: React.ReactNode;
  gasRedundancy: React.ReactNode;
  injectPipelines: NearbyPipeline[];
  suppressCarveOutBox: boolean;
  suppressZonePrecedent: boolean;
}

// ---- Paces Creek Woodlands LP — Jasper Co, TX --------------------------------
// 4-parcel single-owner assembly · 2,356 ac (HomeLand listing; polygon SUM 2,371.85) · Gulf South Pipeline corridor
// East Texas gas corridor. 56 mi from Project Lufkin.

const PACES_PARCEL_IDS = new Set([
  'a9ec56ff-f6dc-490d-9ae0-fabfa790081f',
  'd1236816-e201-48b1-9680-38902302968c',
  'c98cd978-6a07-48e4-9546-d3704d0923bc',
  'e2324b24-70ef-401a-a28d-fc223b6c4650',
]);

const PACES_OVERRIDES: ParcelOverrides = {
  suppressCarveOutBox: true,
  suppressZonePrecedent: true,
  precedentsMeta: 'TX BTM precedents · side-by-side',
  precedentCompare: {
    intro: (
      <>
        Three Texas precedents stress-test the Paces thesis from three angles: the working
        playbook 56 miles northwest, the East-TX-attainment PSD path under organized opposition, and
        the largest BTM air permit ever issued in the United States. Paces sits in the same
        East Texas gas corridor as the in-AOI precedent, with single-owner land that no
        precedent matched.
      </>
    ),
    rows: [
      {
        label: 'Amp Z Project Lufkin',
        sub: 'Angelina Co · 56 mi NW · in-AOI',
        context:
          '$1.05B committed. Same East Texas corridor and TCEQ playbook this site inherits.',
        capacity: '2.1 GW IT',
        acres: '1,041 (→ 4,000)',
        acPerGw: '~496',
        gas: 'Hybrid: Entergy grid + BTM gas. Same East Texas gas corridor.',
        permit: 'TCEQ in flight; ERCOT Batch Zero; no county zoning.',
        land: 'Site under option (private/family).',
        inAoi: true,
      },
      {
        label: 'Sandow Lakes Energy',
        sub: 'Lee Co · ~250 mi NW',
        context:
          'Closest regulatory analog: same NAAQS-attainment air shed, same PSD path.',
        capacity: '1.2 GW CCGT',
        acres: 'Master-site lease',
        acPerGw: 'n/a',
        gas: 'Combined-cycle natural gas; reusing Alcoa Sandow infrastructure.',
        permit: 'TCEQ PSD ~14 mo. Contested-case request denied by TCEQ 90-sec vote, Oct 2025.',
        land: 'Sandow Lakes master site (lease).',
      },
      {
        label: 'Pacifico GW Ranch',
        sub: 'Pecos Co · ~600 mi W',
        context: 'Largest US air permit ever issued.',
        capacity: '5 GW firm (7.65 GW permit)',
        acres: '8,000+',
        acPerGw: '~1,600',
        gas: 'Permian gas; dedicated 15-mi 1 Bcf/d line to Waha.',
        permit: 'TCEQ 7.65 GW, Jan 2026.',
        land: 'Pacifico controls.',
      },
      {
        label: 'Paces Creek Woodlands LP',
        sub: 'Jasper Co · target',
        context:
          'On market: $9.78M / $4,150 per ac. Same East Texas gas corridor as Amp Z.',
        capacity: 'Sized to Integra demand',
        acres: '2,356 (4 parcels, 1 owner)',
        acPerGw: 'n/a',
        gas: 'Gulf South 16″ + 14″ on-parcel; TGP 100 30″ at 3 mi.',
        permit: 'Pending. NAAQS attainment for all 7 pollutants.',
        land: 'Single owner: PACES CREEK WOODLANDS LP.',
        emphasis: true,
      },
    ],
    takeaway: (
      <>
        <strong>The headline.</strong> Each precedent had to fit a campus on 1,041 to 8,000+
        acres of single-counterparty land. Paces delivers 2,356 contiguous acres under one
        owner at pre-announcement pricing — the same gas corridor the in-AOI precedent is
        already drawing from, the same regulatory environment the Sandow contested case
        validated, and a TCEQ permit path the Pacifico precedent has stretched to 7.65 GW.
      </>
    ),
  },
  siteIntelligence: [
    {
      heading: 'The Lands',
      body: (
        <>
          Four parcels. One owner. <strong>2,356 contiguous acres</strong> of East Texas timber
          under PACES CREEK WOODLANDS LP. No fragmentation. No counterparties. The block trades
          as one transaction.
        </>
      ),
    },
    {
      heading: 'The Gas',
      body: (
        <>
          <strong>The transmission infrastructure already on-parcel is the position.</strong> A{' '}
          <strong>16″ Gulf South lateral</strong> (Magasco → Call Junction). A{' '}
          <strong>14″ Gulf South main</strong>. Tennessee Gas Pipeline's{' '}
          <strong>30″ TGP 100 system</strong> three miles east. Plus Energy Transfer's
          Brookeland gathering system overlaying the same footprint. These are
          separate operator contacts; available capacity, pressure, interconnection and
          firm transportation remain unverified.
        </>
      ),
    },
    {
      heading: 'The Precedent',
      body: (
        <>
          Amp Z is building <strong>Project Lufkin</strong> right now,{' '}
          <strong>56 miles northwest</strong>. $1.05B. 2.1 GW. Behind-the-meter. Same
          NAAQS-attainment air shed, same TCEQ playbook, same regulatory clock. The next campus
          walks a path that has already been cleared.
        </>
      ),
    },
    {
      heading: 'The Logistics',
      body: (
        <>
          Highway and rail share a corridor <strong>0.6 miles west</strong> of the property.{' '}
          <strong>US-96</strong> is a 4-lane principal arterial. The{' '}
          <strong>BNSF mainline</strong> parallels it. Turbines and compressors offload at the
          rail-side interchange and roll directly to the parcel. No extended final-mile haul.{' '}
          <strong>Houston, 140 miles by I-10</strong>: the gas-turbine capital of North America
          and the service hub for Integra's manufacturer.{' '}
          <strong>Jack Brooks Regional (BPT)</strong> at 73 mi for crew rotations.{' '}
          <strong>George Bush Intercontinental (IAH)</strong> at 125 mi as the global hub.
        </>
      ),
    },
    {
      heading: 'The Framing',
      body: (
        <>
          This is not a parcel that needs a thesis. Project Lufkin is already running the
          playbook 56 miles northwest. Build here and the campus rivals GW Ranch and Fermi.
          The land has not priced in the playbook yet.
        </>
      ),
    },
  ],
  precedents: [
    {
      name: 'Amp Z Project Lufkin',
      highlight: true,
      distance: '56 mi',
      capacity: '2,100 MW',
      operator: 'Amp Z (Carlyle-backed)',
      status: 'announced · in-AOI',
      location: 'Angelina Co, TX',
      detail:
        '$1.05B committed. Day-one ~55 MW from re-energized Paper Mill substation; 1.1 GW grid-connected by 2028; full 2.1 GW by 2029, layering ~1 GW BTM gas on top. Live local stress-test of TX SB 6, PUCT Rule 25.194, and TCEQ permitting in unincorporated Angelina County. Inside the same East Texas gas corridor — the working playbook this site inherits.',
    },
    {
      name: 'Sandow Lakes Energy',
      distance: '~250 mi NW',
      capacity: '1,200 MW',
      operator: 'Sandow Lakes',
      status: 'permitted · ~14 mo PSD',
      location: 'Lee Co, TX',
      detail:
        'CCGT in NAAQS-attainment East-Central TX. "Move the Gas Plant" coalition contested-case request denied by TCEQ Oct 2025 in a 90-second vote — demonstrates the PSD timeline holds even with organized opposition.',
    },
    {
      name: 'Project Matador / Fermi America',
      distance: '~600 mi NW',
      capacity: '6,000 MW',
      operator: 'Fermi America',
      status: 'permitted · ~7 mo expedited PSD',
      location: 'Carson Co, TX',
      detail:
        'Filed Aug 2025, approved Feb 2026. Used $20K Subchapter J expedited surcharge and 90 identical Siemens SGT-800 turbines to simplify TCEQ review. Largest expedited PSD on record.',
    },
    {
      name: 'GW Ranch / Pacifico Energy',
      distance: '~600 mi W',
      capacity: '7,650 MW',
      operator: 'Pacifico Energy',
      status: 'permitted Jan 2026 · ~16 mo PSD',
      location: 'Pecos Co, TX',
      detail:
        'Largest air permit in US history. Remote Permian Basin siting eliminated contested-case risk. Validates TCEQ has signed off on multi-GW BTM at scale.',
    },
  ],
  logistics: (
    <>
      <strong>BNSF Railway mainline parallels US-96 ~0.6 mi west</strong> of the assembly —
      rail and highway share the corridor at the property, so turbines and compressors offload
      at the rail-side interchange and roll directly to the parcel with no extended final-mile
      haul.{' '}
      <strong>US-96</strong> 4-lane principal arterial <strong>~0.6 mi west</strong> of the
      assembly — heavy-haul ready off the property.{' '}
      <strong>Jack Brooks Regional (BPT)</strong> at 73 mi for crew rotations; major hub{' '}
      <strong>George Bush Intercontinental (IAH)</strong> at 125 mi.{' '}
      <strong>Houston</strong> ~140 mi southwest by I-10 — the gas-turbine capital of North
      America and the service hub for Integra's manufacturer. Lufkin (pop. 35K) at 56 mi —
      labor draw without municipal-scale opposition.
    </>
  ),
  gasRedundancy: (
    <>
      {' '}<strong>Two Gulf South transmission lines</strong> cross the assembly directly: the{' '}
      <strong>16″ Magasco → Call Junction</strong> lateral and the{' '}
      <strong>14″ Gulf South main</strong>. Tennessee Gas Pipeline's{' '}
      <strong>30″ TGP 100 System</strong> sits 3.07 mi east as interstate-scale redundancy;
      Energy Transfer's Brookeland gathering system overlays the same footprint. Multi-operator
      on-parcel access at this density is uncommon.
    </>
  ),
  // Keep the reviewed physical lines; source observations and delivery terms are separate.
  injectPipelines: [
    {
      id: '5d92f91a-32a7-44fe-a8ee-62b80053fc54',
      name: 'Gulf South Pipeline Co',
      operator: 'Gulf South Pipeline Co',
      distance_miles: 0,
      capacity_mmcf_day: null,
      mw_potential: null,
      capacity_source: null,
      capacity_basis: 'unavailable',
      source: 'EIA',
      diameter_inches: 14,
      utilization_pct: null,
      utilization_source: null,
      local_utilization_pct: null,
      local_util_source: null,
      flow_volume_mmcf_day: null,
      maop_psig: null,
      maop_source: null,
      system_type: 'transmission',
      pressure_grade: null,
      is_gathering: false,
    },
    {
      id: 'b86f2082-2f83-4dcc-bb62-89c45b912e97',
      name: 'TGP 100 SYSTEM',
      operator: 'Tennessee Gas Pipeline Co, L.L.C.',
      distance_miles: 3.07,
      capacity_mmcf_day: null,
      mw_potential: null,
      capacity_source: null,
      capacity_basis: 'unavailable',
      source: 'EIA',
      diameter_inches: 30,
      utilization_pct: null,
      utilization_source: null,
      local_utilization_pct: null,
      local_util_source: null,
      flow_volume_mmcf_day: null,
      maop_psig: null,
      maop_source: null,
      system_type: 'transmission',
      pressure_grade: null,
      is_gathering: false,
    },
  ],
};

export function getParcelOverrides(parcelId: string): ParcelOverrides | null {
  if (PACES_PARCEL_IDS.has(parcelId)) return PACES_OVERRIDES;
  return null;
}
